import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Boost from "highcharts/modules/boost";
import html2pdf from "html2pdf.js";
import { FaCopy, FaDownload, FaPen, FaRedo } from "react-icons/fa";
import { Offcanvas } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { giveFeedback } from "../../utilities/EmgptUtilities";
import useChatVoice, { htmlToSpeechText } from "../../utilities/useChatVoice";
import {
  getAuthenticatedWebSocketUrl,
  getToken,
} from "../../utilities/authHelper";
import {
  ChartOptions,
  fetchFxSymbols,
  fetchFilteredPrices,
  ForwardChartOptions,
} from "../CoTrader/Utilities/CoTraderUtils";
import {
  getCanonicalForwardPoints,
  getCanonicalForwardTenors,
} from "../CoTrader/Utilities/forwardTenors";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import ChatOverlayFrame from "../Common/ChatOverlayFrame";
import EmLoader from "../Common/EmLoader";
import ChartLoader from "../Common/ChartLoader";
import { RenderFromString } from "../CoTrader/CoTraderEmgpt";
import { useResponsiveTileTypography } from "../CoTrader/Utilities/useResponsiveTileTypography";
import "../Common/chatSurface.css";
import "../CoTrader/CoTrader.css";
import "./TradeAnalysisLanding.css";
import TradeAnalysisRenderFromString from "./TradeAnalysisRenderFromString";
import {
  getDefaultTradeExecutionTimestamp,
  getDefaultTradeExpiryTimestamp,
} from "./tradeAnalysisDefaults";

Boost(Highcharts);

const TRADE_TYPE_OPTIONS = [
  { label: "BUY", value: "BUY" },
  { label: "SELL", value: "SELL" },
];
const TRADE_ANALYSIS_CHART_HEIGHT = 260;
const TRADE_ANALYSIS_PRICE_WINDOW_MS = 60 * 60 * 1000;
const TRADE_ANALYSIS_SURFACE = "trade_analysis";
const TRADE_ANALYSIS_ORIGIN = "TRADE_ANALYSIS";
const MARKET_WINDOW_ANALYSIS_SOURCES = {
  prices: true,
  trading_economics: true,
  realtime_news: true,
  truth_social: false,
};

const parseSummaryPayload = (message) => {
  if (message == null) return null;

  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message;
};

const getPalette = (isDark) => {
  if (isDark) {
    return {
      pageBg: "#0B0F1A",
      cardBg: "#12172A",
      cardInnerBg: "rgba(255,255,255,0.06)",
      cardBorder: "#1E2642",
      textPrimary: "#E8ECF8",
      textSecondary: "#A6B0CF",
      textMuted: "#7F8AAD",
      accent: "#4C7DFF",
      accentHover: "#3F6BE2",
      danger: "#FF6B6B",
      surfaceLight: "#F8FAFF",
      surfaceDark: "#0F1424",
      inputBg: "#0F1424",
      inputBorder: "#2A2F45",
    };
  }

  return {
    pageBg: "#F3F5FB",
    cardBg: "#FFFFFF",
    cardInnerBg: "#FFFFFF",
    cardBorder: "#DDE3F0",
    textPrimary: "#0F1A34",
    textSecondary: "#4E5C79",
    textMuted: "#7B879F",
    accent: "#0D6EFD",
    accentHover: "#0B5ED7",
    danger: "#DC3545",
    surfaceLight: "#FFFFFF",
    surfaceDark: "#F9FBFF",
    inputBg: "#FFFFFF",
    inputBorder: "#CED6EA",
  };
};

const setSessionVariable = (currencyPair) => {
  if (currencyPair) {
    localStorage.setItem("EMALPHA_DASHBAR_CURRENCY_PAIR", currencyPair);
  }
};

const normalizeNumber = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const getUserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Calcutta";

const createTradeAnalysisId = () => `TRADEANALYSISGPT-${crypto.randomUUID()}`;

const DATE_TIME_CAPTURE = /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/;

const toDateTimeLocalValue = (value) => {
  if (!value) return "";

  const trimmed = String(value).trim();
  if (!trimmed) return "";

  const match = trimmed.match(DATE_TIME_CAPTURE);
  if (!match) return "";

  return `${match[1]}T${match[2]}`;
};

const fromDateTimeLocalValue = (value) => {
  if (!value) return "";

  const trimmed = String(value).trim();
  if (!trimmed) return "";

  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/);
  if (!match) return trimmed;

  const seconds = match[3] ?? "00";
  return `${match[1]} ${match[2]}:${seconds}`;
};

const padDatePart = (value) => String(value).padStart(2, "0");

const toDateTimeLocalMinuteValue = (date) => {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate(),
  )}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const getExecutionTimestampBounds = () => {
  const now = new Date();
  now.setSeconds(0, 0);

  const twoMonthsAgo = new Date(now);
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

  return {
    minExecutionTimestamp: toDateTimeLocalMinuteValue(twoMonthsAgo),
    maxExecutionTimestamp: toDateTimeLocalMinuteValue(now),
  };
};

const areArraysEqual = (arr1 = [], arr2 = []) => {
  if (arr1.length !== arr2.length) return false;
  for (let index = 0; index < arr1.length; index += 1) {
    if (arr1[index] !== arr2[index]) return false;
  }
  return true;
};

const fetchForwardPointsForTimestamp = async ({
  setForwardPoints,
  symbol,
  setTenorList,
  executionTimestamp,
}) => {
  if (!symbol || !executionTimestamp) return;

  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(
      `${apiUrl}/globallink/prices/get_forward_points?symbol=${symbol}&timestamp=${encodeURIComponent(executionTimestamp)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch forward points");
    }

    const data = await response.json();
    const orderedForwardPoints = getCanonicalForwardPoints(
      data?.results?.latest_tenor_prices || [],
    );
    const orderedTenorList = getCanonicalForwardTenors(
      orderedForwardPoints.map((item) => item.tenor),
    );

    setTenorList(orderedTenorList);
    setForwardPoints(orderedForwardPoints);
  } catch (error) {
    console.error("Error fetching forward points for timestamp:", error);
  }
};

// Ticket values stay as "YYYY-MM-DDTHH:mm" wall-clock strings (the same shape
// the native datetime-local input produced), so validation/submit are unchanged.
const parseTicketDateTime = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  return new Date(year, month - 1, day, hour, minute);
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => padDatePart(index));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => padDatePart(index));

// react-datepicker injects `value` ("HH:mm") and `onChange` via customTimeInput.
// Replaces the native time input, which Safari renders misaligned and in 12h.
const TicketTimeInput = ({ value, onChange }) => {
  const [hour = "00", minute = "00"] = String(value || "").split(":");

  return (
    <div className="ta-time">
      <select
        className="ta-time-select"
        aria-label="Hour"
        value={hour}
        onChange={(event) => onChange(`${event.target.value}:${minute}`)}
      >
        {HOUR_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="ta-time-sep" aria-hidden="true">:</span>
      <select
        className="ta-time-select"
        aria-label="Minute"
        value={minute}
        onChange={(event) => onChange(`${hour}:${event.target.value}`)}
      >
        {MINUTE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="ta-time-zone">UTC</span>
    </div>
  );
};

const TicketDateTimePicker = ({
  id,
  value,
  onChange,
  min,
  max,
  placeholder = "Select date & time",
  isClearable = false,
}) => (
  <div className="ta-datetime">
    <i className="fa-regular fa-calendar ta-datetime-icon" aria-hidden="true" />
    <DatePicker
      id={id}
      selected={parseTicketDateTime(value)}
      onChange={(date) => onChange(date ? toDateTimeLocalMinuteValue(date) : "")}
      minDate={parseTicketDateTime(min)}
      maxDate={parseTicketDateTime(max)}
      showTimeInput
      timeInputLabel="Time"
      customTimeInput={<TicketTimeInput />}
      dateFormat="MMM d, yyyy  HH:mm"
      placeholderText={placeholder}
      isClearable={isClearable}
      className="form-control ta-input"
      calendarClassName="ta-datepicker"
      popperPlacement="bottom-start"
      portalId="ta-datepicker-portal"
      autoComplete="off"
    />
  </div>
);

const TicketField = ({ id, label, hint, error, className = "", children }) => (
  <div className={`ta-field ${className}`.trim()}>
    <label className="ta-label" htmlFor={id}>
      {label}
      {hint ? <span className="ta-label-hint">{hint}</span> : null}
    </label>
    {children}
    {error ? <p className="ta-error">{error}</p> : null}
  </div>
);

const TradeAnalysisLandingPage = ({ currencyList, onSubmit, isMobile = false }) => {
  const [currencyPair, setCurrencyPair] = useState("");
  const [tradeType, setTradeType] = useState("BUY");
  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState("");
  const [executionForwardPoints, setExecutionForwardPoints] = useState("");
  const [expirationTimestamp, setExpirationTimestamp] = useState("");
  const [errors, setErrors] = useState({});

  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds();
  const analysisOutputs = [
    {
      title: "Execution Readout",
      body: "Explain whether the trade was done into strength, weakness, or noisy market conditions.",
    },
    {
      title: "Market Context",
      body: "Pull the spot window, forward curve behavior, and timing backdrop into one narrative.",
    },
    {
      title: "Analyst Follow-Up",
      body: "Keep a live chat session open so the desk can challenge assumptions or generate a report.",
    },
  ];

  const validate = () => {
    const next = {};
    if (!currencyPair) next.currencyPair = "Currency pair is required";

    if (!executionTimestamp.trim()) {
      next.executionTimestamp = "Execution timestamp is required";
    } else if (
      executionTimestamp < minExecutionTimestamp ||
      executionTimestamp > maxExecutionTimestamp
    ) {
      next.executionTimestamp =
        "Execution timestamp must be within the last 2 months and not in the future";
    }

    if (
      expirationTimestamp &&
      executionTimestamp &&
      expirationTimestamp < executionTimestamp
    ) {
      next.expirationTimestamp =
        "Expiration date must be greater than or equal to execution timestamp";
    }

    if (!tradeType) next.tradeType = "Trade type is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const parsedRate = Number(executionSpotRate);
    const parsedFwd = Number(executionForwardPoints);

    onSubmit({
      currencyPair,
      tradeType,
      executionTimestamp: fromDateTimeLocalValue(executionTimestamp),
      executionSpotRate: Number.isFinite(parsedRate) ? parsedRate : null,
      executionForwardPoints: Number.isFinite(parsedFwd) ? parsedFwd : null,
      expirationTimestamp: fromDateTimeLocalValue(expirationTimestamp),
    });
  };

  return (
    <div className={`ta-landing ct-page w-100 h-100${isMobile ? " is-mobile" : ""}`}>
      <form
        className="ta-ticket"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <header className="ta-ticket-head">
          <span className="ta-ticket-icon" aria-hidden="true">
            <i className="fa-solid fa-receipt" />
          </span>
          <div className="ta-ticket-heading">
            <h1 className="ta-ticket-title">New trade ticket</h1>
            <p className="ta-ticket-sub">
              Log an FX execution to review its verdict, market context and analyst chat.
            </p>
          </div>
        </header>

        <div className="ta-ticket-body">
          <div className="ta-fields">
            <p className="ta-section">Instrument</p>
            <div className="ta-field-row">
              <TicketField
                id="ta-currency-pair"
                label="Currency Pair"
                error={errors.currencyPair}
              >
                <select
                  id="ta-currency-pair"
                  className="form-select ta-input"
                  value={currencyPair}
                  data-empty={!currencyPair}
                  onChange={(event) => {
                    setCurrencyPair(event.target.value);
                    setErrors((prev) => ({ ...prev, currencyPair: "" }));
                  }}
                >
                  <option value="">Select currency pair</option>
                  {currencyList.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </TicketField>

              <TicketField id="ta-trade-type" label="Side" error={errors.tradeType}>
                <div
                  id="ta-trade-type"
                  className="ta-side"
                  role="radiogroup"
                  aria-label="Side"
                >
                  {TRADE_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={tradeType === option.value}
                      className={`ta-side-btn ta-side-btn--${option.value.toLowerCase()}${
                        tradeType === option.value ? " is-active" : ""
                      }`}
                      onClick={() => {
                        setTradeType(option.value);
                        setErrors((prev) => ({ ...prev, tradeType: "" }));
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </TicketField>
            </div>

            <p className="ta-section">Execution</p>
            <TicketField
              id="ta-execution-timestamp"
              label="Timestamp"
              hint="UTC"
              error={errors.executionTimestamp}
            >
              <TicketDateTimePicker
                id="ta-execution-timestamp"
                value={executionTimestamp}
                min={minExecutionTimestamp}
                max={maxExecutionTimestamp}
                onChange={(nextValue) => {
                  setExecutionTimestamp(nextValue);
                  setErrors((prev) => ({
                    ...prev,
                    executionTimestamp: "",
                    expirationTimestamp: "",
                  }));
                }}
              />
            </TicketField>

            <div className="ta-field-row">
              <TicketField
                id="ta-spot-rate"
                label="Spot Rate"
                hint="Optional"
              >
                <input
                  id="ta-spot-rate"
                  className="form-control ta-input"
                  value={executionSpotRate}
                  onChange={(event) => setExecutionSpotRate(event.target.value)}
                  inputMode="decimal"
                  placeholder="e.g. 157.96"
                />
              </TicketField>

              <TicketField id="ta-forward-points" label="Forward Points" hint="Optional">
                <input
                  id="ta-forward-points"
                  className="form-control ta-input"
                  value={executionForwardPoints}
                  onChange={(event) => setExecutionForwardPoints(event.target.value)}
                  inputMode="decimal"
                  placeholder="e.g. -0.0002"
                />
              </TicketField>
            </div>

            <p className="ta-section">Expiry</p>
            <TicketField
              id="ta-expiration"
              label="Expiration"
              hint="Optional · UTC"
              error={errors.expirationTimestamp}
            >
              <TicketDateTimePicker
                id="ta-expiration"
                value={expirationTimestamp}
                min={executionTimestamp || undefined}
                placeholder="No expiry"
                isClearable
                onChange={(nextValue) => {
                  setExpirationTimestamp(nextValue);
                  setErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
                }}
              />
            </TicketField>
          </div>

          <button type="submit" className="ta-submit">
            Analyze trade
          </button>
        </div>

        <footer className="ta-ticket-foot">
          <p className="ta-foot-label">After you submit</p>
          <div className="ta-outputs">
            {analysisOutputs.map((item) => (
              <div key={item.title} className="ta-output">
                <p className="ta-output-title">{item.title}</p>
                <p className="ta-output-body">{item.body}</p>
              </div>
            ))}
          </div>
        </footer>
      </form>
    </div>
  );
};

const TradeAnalysisTradeHeader = ({
  currencyPair,
  tradeType,
  executionTimestampUtc,
  executionSpotRate,
  executionForwardPoints,
  expiryDateUtc,
  palette,
  isMobile = false,
}) => {
  const formatNumber = (value, decimalPlaces = 5) => {
    if (!Number.isFinite(value)) return "—";
    return value.toFixed(decimalPlaces);
  };

  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    padding: "5px 10px",
    border: `1px solid ${palette.cardBorder}`,
    backgroundColor: palette.cardInnerBg,
    color: palette.textPrimary,
    fontSize: "0.75rem",
    lineHeight: "1rem",
    whiteSpace: isMobile ? "normal" : "nowrap",
    maxWidth: isMobile ? "100%" : "none",
  };

  const buySellStyle =
    tradeType === "BUY"
      ? {
          backgroundColor: "rgba(46, 213, 115, 0.18)",
          border: "1px solid rgba(46, 213, 115, 0.45)",
        }
      : {
          backgroundColor: "rgba(220, 53, 69, 0.18)",
          border: "1px solid rgba(220, 53, 69, 0.45)",
        };

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center" style={{ minWidth: 0 }}>
      <span style={chipStyle}>
        <span style={{ color: palette.textMuted }}>Pair</span>
        <span style={{ fontWeight: 700 }}>{currencyPair}</span>
      </span>

      <span style={{ ...chipStyle, ...buySellStyle }}>
        <span style={{ color: palette.textMuted }}>Type</span>
        <span style={{ fontWeight: 700 }}>{tradeType}</span>
      </span>

      <span style={chipStyle}>
        <span style={{ color: palette.textMuted }}>Exec Timestamp</span>
        <span style={{ fontWeight: 700 }}>{executionTimestampUtc}</span>
      </span>

      {!isMobile && (
        <>
          <span style={chipStyle}>
            <span style={{ color: palette.textMuted }}>Exec Spot Rate</span>
            <span style={{ fontWeight: 700 }}>
              {executionSpotRate == null
                ? "—"
                : formatNumber(
                    Number(executionSpotRate),
                    currencyPair?.includes("JPY") ? 3 : 5,
                  )}
            </span>
          </span>

          <span style={chipStyle}>
            <span style={{ color: palette.textMuted }}>Exec Forward Point</span>
            <span style={{ fontWeight: 700 }}>
              {executionForwardPoints == null
                ? "—"
                : formatNumber(
                    Number(executionForwardPoints),
                    currencyPair?.includes("JPY") ? 3 : 5,
                  )}
            </span>
          </span>

          <span style={chipStyle}>
            <span style={{ color: palette.textMuted }}>Expiry</span>
            <span style={{ fontWeight: 700 }}>{expiryDateUtc || "—"}</span>
          </span>
        </>
      )}
    </div>
  );
};

const TradeAnalysisPopOverMenu = ({
  setCurrencyPair,
  setUseContext,
  useContext,
  currencyPair,
  currencyList,
  tenorList,
  setTenor,
  setUserLanguage,
  userLanguage,
  setExecutionTimestamp,
  setExecutionSpotRate,
  setExpirationDate,
  setExecutionForwardPoints,
  tradeType,
  setTradeType,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
  palette,
  isMobile = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(
    useContext ? "AUTO" : currencyPair || "AUTO",
  );
  const [selectedTenors, setSelectedTenors] = useState([]);

  const menuContainerRef = useRef(null);
  const popoverRef = useRef(null);

  const [draftTradeType, setDraftTradeType] = useState(tradeType);
  const [draftExecTs, setDraftExecTs] = useState(() =>
    toDateTimeLocalValue(
      executionTimestamp ||
      localStorage.getItem("trade_analysis_exec_ts") ||
        getDefaultTradeExecutionTimestamp(),
    ),
  );
  const [draftExecRate, setDraftExecRate] = useState(
    executionSpotRate != null
      ? String(executionSpotRate)
      : localStorage.getItem("trade_analysis_exec_rate") || "157.96",
  );
  const [draftExecFwd, setDraftExecFwd] = useState(
    executionForwardPoints != null
      ? String(executionForwardPoints)
      : localStorage.getItem("trade_analysis_exec_fwd") || "-0.0002",
  );
  const [draftExpiry, setDraftExpiry] = useState(() =>
    toDateTimeLocalValue(
      expirationTimestamp ||
      localStorage.getItem("trade_analysis_expiry") ||
        getDefaultTradeExpiryTimestamp(),
    ),
  );

  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds();
  const [draftErrors, setDraftErrors] = useState({
    executionTimestamp: "",
    expirationTimestamp: "",
  });

  useEffect(() => {
    if (isMobile) return undefined;

    const handleOutsideClick = (event) => {
      if (!isModalOpen) return;

      const target = event.target;
      const insidePopper = popoverRef.current?.contains(target);
      const insideMenuIcon = menuContainerRef.current?.contains(target);

      if (!insidePopper && !insideMenuIcon) {
        setIsModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
    };
  }, [isModalOpen, isMobile]);

  useEffect(() => {
    const nextTenors = getCanonicalForwardTenors(tenorList);

    if (!areArraysEqual(selectedTenors, nextTenors)) {
      setSelectedTenors(nextTenors);
      setTenor?.(nextTenors);
    }
  }, [selectedTenors, setTenor, tenorList]);

  useEffect(() => {
    if (isModalOpen) return;

    setSelectedCurrency(useContext ? "AUTO" : currencyPair || "AUTO");
    setDraftTradeType(tradeType);
    setDraftExecTs(toDateTimeLocalValue(executionTimestamp || ""));
    setDraftExecRate(executionSpotRate != null ? String(executionSpotRate) : "");
    setDraftExecFwd(
      executionForwardPoints != null ? String(executionForwardPoints) : "",
    );
    setDraftExpiry(toDateTimeLocalValue(expirationTimestamp || ""));
  }, [
    isModalOpen,
    useContext,
    currencyPair,
    tradeType,
    executionTimestamp,
    executionSpotRate,
    executionForwardPoints,
    expirationTimestamp,
  ]);

  const handleSubmit = () => {
    const nextErrors = {
      executionTimestamp: "",
      expirationTimestamp: "",
    };

    if (!draftExecTs) {
      nextErrors.executionTimestamp = "Execution timestamp is required";
    } else if (
      draftExecTs < minExecutionTimestamp ||
      draftExecTs > maxExecutionTimestamp
    ) {
      nextErrors.executionTimestamp =
        "Execution timestamp must be within the last 2 months and not in the future";
    }

    if (draftExpiry && draftExecTs && draftExpiry < draftExecTs) {
      nextErrors.expirationTimestamp =
        "Expiration date must be greater than or equal to execution timestamp";
    }

    if (nextErrors.executionTimestamp || nextErrors.expirationTimestamp) {
      setDraftErrors(nextErrors);
      return;
    }

    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });

    if (selectedCurrency !== "AUTO") {
      setCurrencyPair(selectedCurrency);
      setUseContext(false);
    } else {
      setUseContext(true);
    }

    const executionTimestampValue = fromDateTimeLocalValue(draftExecTs);
    const expiryTimestampValue = fromDateTimeLocalValue(draftExpiry);

    setTradeType(draftTradeType);
    setExecutionTimestamp(executionTimestampValue);
    setExecutionSpotRate(normalizeNumber(draftExecRate));
    setExecutionForwardPoints(normalizeNumber(draftExecFwd));
    setExpirationDate(expiryTimestampValue);

    localStorage.setItem("trade_analysis_exec_ts", executionTimestampValue);
    localStorage.setItem("trade_analysis_exec_rate", draftExecRate);
    localStorage.setItem("trade_analysis_exec_fwd", draftExecFwd);
    localStorage.setItem("trade_analysis_expiry", expiryTimestampValue);

    setIsModalOpen(false);
  };

  const handleReset = () => {
    setSelectedCurrency(useContext ? "AUTO" : currencyPair || "AUTO");
    setDraftTradeType(tradeType);
    setDraftExecTs(toDateTimeLocalValue(executionTimestamp || ""));
    setDraftExecRate(executionSpotRate != null ? String(executionSpotRate) : "");
    setDraftExecFwd(
      executionForwardPoints != null ? String(executionForwardPoints) : "",
    );
    setDraftExpiry(toDateTimeLocalValue(expirationTimestamp || ""));
    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });
  };

  const filterForm = (
    <>
      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Currency Pair
      </div>
      <select
        className="form-select mb-3"
        value={selectedCurrency}
        onChange={(event) => setSelectedCurrency(event.target.value)}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
      >
        <option value="AUTO">AUTO</option>
        {currencyList.map((currency) => (
          <option key={currency} value={currency}>
            {currency}
          </option>
        ))}
      </select>

      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Trade Type
      </div>
      <select
        className="form-select mb-3"
        value={draftTradeType}
        onChange={(event) => setDraftTradeType(event.target.value)}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
      >
        {TRADE_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Execution Timestamp (UTC)
      </div>
      <input
        className="form-control mb-2"
        type="datetime-local"
        step={60}
        min={minExecutionTimestamp}
        max={maxExecutionTimestamp}
        value={draftExecTs}
        onChange={(event) => {
          setDraftExecTs(event.target.value);
          setDraftErrors((prev) => ({
            ...prev,
            executionTimestamp: "",
            expirationTimestamp: "",
          }));
        }}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
      />
      {draftErrors.executionTimestamp && (
        <div className="mb-2" style={{ color: palette.danger, fontSize: "0.75rem" }}>
          {draftErrors.executionTimestamp}
        </div>
      )}
      <div className="mb-3" style={{ fontSize: "0.72rem", color: palette.textMuted }}>
        Select date and time directly.
      </div>

      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Execution Spot Rate
      </div>
      <input
        className="form-control mb-3"
        value={draftExecRate}
        onChange={(event) => setDraftExecRate(event.target.value)}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
        placeholder="e.g. 157.96"
        inputMode="decimal"
      />

      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Execution Forward Point
      </div>
      <input
        className="form-control mb-3"
        value={draftExecFwd}
        onChange={(event) => setDraftExecFwd(event.target.value)}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
        placeholder="e.g. -0.0002"
        inputMode="decimal"
      />

      <div className="mb-2" style={{ color: palette.textSecondary, fontWeight: 700, fontSize: "0.82rem" }}>
        Expiration Date
      </div>
      <input
        className="form-control mb-3"
        type="datetime-local"
        step={60}
        min={draftExecTs || undefined}
        value={draftExpiry}
        onChange={(event) => {
          setDraftExpiry(event.target.value);
          setDraftErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
        }}
        style={{
          backgroundColor: palette.inputBg,
          borderColor: palette.inputBorder,
          color: palette.textPrimary,
        }}
      />
      {draftErrors.expirationTimestamp && (
        <div className="mb-2" style={{ color: palette.danger, fontSize: "0.75rem" }}>
          {draftErrors.expirationTimestamp}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm"
          onClick={handleReset}
          style={{
            border: `1px solid ${palette.cardBorder}`,
            color: palette.textSecondary,
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={handleSubmit}
          style={{
            backgroundColor: palette.accent,
            color: "#fff",
            borderColor: palette.accent,
            fontWeight: 600,
          }}
        >
          Submit
        </button>
      </div>
    </>
  );

  return (
    <div className="position-relative" ref={menuContainerRef}>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setIsModalOpen((prev) => !prev)}
        style={{ color: palette.textSecondary }}
      >
        <i className="fa-solid fa-bars" />
      </button>

      {!isMobile && isModalOpen && (
        <div
          ref={popoverRef}
          className="position-absolute end-0 mt-2"
          style={{
            width: "320px",
            maxWidth: "calc(100vw - 30px)",
            zIndex: 30,
            borderRadius: "12px",
            border: `1px solid ${palette.cardBorder}`,
            backgroundColor: palette.cardBg,
            boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
            padding: "14px",
          }}
        >
          {filterForm}
        </div>
      )}

      {isMobile && (
        <Offcanvas
          show={isModalOpen}
          onHide={() => setIsModalOpen(false)}
          placement="bottom"
          style={{
            height: "88%",
            backgroundColor: palette.cardBg,
            borderTop: `1px solid ${palette.cardBorder}`,
          }}
        >
          <Offcanvas.Header
            closeButton
            style={{ borderBottom: `1px solid ${palette.cardBorder}` }}
          >
            <Offcanvas.Title style={{ color: palette.textPrimary, fontWeight: 700 }}>
              Trade Filters
            </Offcanvas.Title>
            <style>{`
              .offcanvas-header .btn-close {
                filter: ${palette.pageBg === "#0B0F1A" ? "invert(1)" : "none"};
              }
            `}</style>
          </Offcanvas.Header>
          <Offcanvas.Body style={{ overflowY: "auto", paddingBottom: "70px" }}>
            {filterForm}
          </Offcanvas.Body>
        </Offcanvas>
      )}
    </div>
  );
};

const SpotPriceChart = React.memo(({ currencyPair, executionTimestamp, isDark, height = TRADE_ANALYSIS_CHART_HEIGHT }) => {
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currencyPair || !executionTimestamp) return undefined;

    const executionDate = new Date(executionTimestamp);
    if (Number.isNaN(executionDate.getTime())) return undefined;

    const startTimeStamp = new Date(
      executionDate.getTime() - TRADE_ANALYSIS_PRICE_WINDOW_MS,
    );
    const endTimeStamp = new Date(
      executionDate.getTime() + TRADE_ANALYSIS_PRICE_WINDOW_MS,
    );

    let active = true;
    setIsLoading(true);
    fetchFilteredPrices({
      symbol: currencyPair,
      setPricesData: setChartData,
      startDate: startTimeStamp.toISOString(),
      endDate: endTimeStamp.toISOString(),
      tenor: "ON",
      useUtcDateTimeRange: true,
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [currencyPair, executionTimestamp]);

  const chartOptions = useMemo(
    () => {
      const baseOptions = ChartOptions({
        prices: chartData,
        isDark,
        showBidAsk: false,
      });

      return {
        ...baseOptions,
        chart: {
          ...(baseOptions.chart || {}),
          height,
          animation: false,
        },
      };
    },
    [chartData, isDark, height],
  );

  return (
    <div style={{ position: "relative" }}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      {isLoading && <ChartLoader dim isDark={isDark} />}
    </div>
  );
});

const ForwardPointsChart = React.memo(({
  currencyPair,
  executionTimestamp,
  tenors,
  setTenorList,
  isDark,
  height = TRADE_ANALYSIS_CHART_HEIGHT,
}) => {
  const [forwardPoints, setForwardPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currencyPair || !executionTimestamp) return undefined;

    let active = true;
    setIsLoading(true);
    fetchForwardPointsForTimestamp({
      setForwardPoints,
      symbol: currencyPair,
      setTenorList,
      executionTimestamp,
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [currencyPair, executionTimestamp, setTenorList]);

  const filteredForwardPoints = useMemo(
    () => getCanonicalForwardPoints(forwardPoints),
    [forwardPoints],
  );
  const autoTenors = useMemo(
    () => getCanonicalForwardTenors(filteredForwardPoints.map((item) => item.tenor)),
    [filteredForwardPoints],
  );
  const selectedTenors = useMemo(
    () => getCanonicalForwardTenors(tenors || []),
    [tenors],
  );

  const chartOptions = useMemo(
    () => {
      const baseOptions = ForwardChartOptions({
        forwardPoints: filteredForwardPoints,
        tenor: selectedTenors.length > 0 ? selectedTenors : autoTenors,
        isDark,
      });

      return {
        ...baseOptions,
        chart: {
          ...(baseOptions.chart || {}),
          height,
          animation: false,
        },
      };
    },
    [autoTenors, filteredForwardPoints, height, isDark, selectedTenors],
  );

  return (
    <div style={{ position: "relative" }}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      {isLoading && <ChartLoader dim isDark={isDark} />}
    </div>
  );
});

const mdToHtml = (md) => {
  const escapeHtml = (value) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const applyInline = (value) => {
    let output = value;
    output = output.replace(/`([^`]+)`/g, "<code>$1</code>");
    output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    output = output.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    return output;
  };

  const lines = (md || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let paragraph = [];
  let listItems = [];
  let listType = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = applyInline(escapeHtml(paragraph.join(" ")).trim());
    out.push(`<p>${text}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0 || !listType) return;
    const tag = listType;
    const items = listItems
      .map((item) => `<li>${applyInline(escapeHtml(item))}</li>`)
      .join("\n");
    out.push(`<${tag}>\n${items}\n</${tag}>`);
    listItems = [];
    listType = null;
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      const text = applyInline(escapeHtml(headingMatch[2]));
      out.push(`<h${level}>${text}</h${level}>`);
      continue;
    }

    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch && !line.startsWith("**")) {
      flushParagraph();
      if (listType === "ol") flushList();
      listType = "ul";
      listItems.push(ulMatch[1]);
      continue;
    }

    const olMatch = line.match(/^\d+[.)]\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      if (listType === "ul") flushList();
      listType = "ol";
      listItems.push(olMatch[1]);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return out.join("\n");
};

const TradeAnalysisSummary = ({ summary, loading, palette }) => {
  const endRef = useRef(null);
  const { containerRef: summaryShellRef, tier: summaryTier } =
    useResponsiveTileTypography();

  const summaryHtml = useMemo(() => mdToHtml(summary || ""), [summary]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [loading, summaryHtml]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(summary || "");
      alert("Copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <div
      ref={summaryShellRef}
      data-tier={summaryTier}
      className="d-flex flex-column ta-summary"
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        borderRadius: "10px",
        border: `1px solid ${palette.cardBorder}`,
        backgroundColor: palette.cardBg,
        padding: "10px",
      }}
    >
      <div className="d-flex align-items-center justify-content-between gap-2 px-1 pb-2 flex-wrap">
        <div>
          <p className="mb-0" style={{ color: palette.textPrimary, fontWeight: 700, fontSize: "0.9rem" }}>
            Trade Summary
          </p>
          <p className="mb-0" style={{ color: palette.textSecondary, fontSize: "0.75rem" }}>
            Trade Context &amp; Reconciliation
          </p>
        </div>

        <button
          type="button"
          className="btn btn-sm"
          onClick={copyToClipboard}
          style={{ border: `1px solid ${palette.cardBorder}`, color: palette.textSecondary }}
        >
          <FaCopy className="me-2" />
          Copy
        </button>
      </div>

      <div
        className="flex-grow-1"
        style={{
          minHeight: 0,
          overflow: "auto",
          borderRadius: "8px",
          border: "1px solid var(--ct-border)",
          backgroundColor: "var(--ct-surface-alt)",
          padding: "12px",
          color: "var(--ct-text)",
        }}
      >
        <div
          className="ta-summary-body"
          style={{ lineHeight: 1.55 }}
          dangerouslySetInnerHTML={{ __html: summaryHtml }}
        />

        {loading && (
          <div
            className={`d-flex align-items-center justify-content-center ${
              summaryHtml ? "mt-3" : "h-100"
            }`}
            style={summaryHtml ? undefined : { minHeight: "100%" }}
          >
            <EmLoader size="lg" label="Generating trade summary" />
          </div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
};

const escapeHtml = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toPlainText = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

const formatUrlList = (rawUrls) => {
  const urls = rawUrls.split(/\s+/).filter(Boolean);
  if (urls.length === 0) return "";

  const links = urls
    .map((url) => {
      const safeUrl = escapeHtml(url);
      return `<a href="${safeUrl}" target="_blank" rel="noreferrer">${safeUrl}</a>`;
    })
    .join("<br />");

  return `<div><strong>Sources</strong><br />${links}</div>`;
};

const hasHtmlTags = (value) => /<\/?[a-z][\s\S]*>/i.test(value);

const wrapSpecialPayload = (title, html) =>
  `<section><h3>${escapeHtml(title)}</h3>${html}</section>`;

const appendSpecialPayload = (current, variant, title, payload) => {
  if (current.variant === variant) {
    return {
      ...current,
      text: `${current.text}${payload}`,
    };
  }

  if (hasHtmlTags(payload)) {
    return {
      ...current,
      variant,
      text: `${current.text}${wrapSpecialPayload(title, payload)}`,
    };
  }

  return {
    ...current,
    variant,
    text: `${current.text}### ${title}\n\n${payload}`,
  };
};

// Seeds the analysis-steps block on send; INFO frames append beneath it.
const PROCESSING_STEP = "Processing\n";

const buildSuggestions = (currencyPair, tradeType) => [
  {
    prompt: `Analyze this ${tradeType} ${currencyPair} execution and explain what moved the market in the execution window.`,
    requestType: "market_window",
  },
  {
    prompt: `Summarize the main risks and drivers for this ${tradeType} ${currencyPair} trade.`,
    requestType: "conversation",
  },
  {
    prompt: "Ignore lower-confidence sources and focus on higher-quality market evidence.",
    requestType: "conversation",
  },
  { prompt: "Generate the final report.", requestType: "conversation" },
];

const TradeAnalysisConversation = ({
  currencyPair,
  tradeType,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
  userLanguage,
  palette,
}) => {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState([]);
  const [feedbackTypes, setFeedbackTypes] = useState({});
  const [feedbackTexts, setFeedbackTexts] = useState({});
  const { containerRef: chatShellRef, tier: chatTier } =
    useResponsiveTileTypography();
  const {
    clearSpeechError,
    isListening,
    isSpeechRecognitionSupported,
    isSpeechSynthesisSupported,
    isTtsEnabled,
    resetVoiceInput,
    speakStreamingText,
    speechError,
    stopListening,
    stopSpeaking,
    toggleListening,
    toggleTts,
  } = useChatVoice({
    userInput: messageInput,
    setUserInput: setMessageInput,
    userLanguage,
  });
  const [sessionNotice, setSessionNotice] = useState(
    "Trade Analysis conversation is available here. Enter your query to begin.",
  );
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const chatIdRef = useRef(createTradeAnalysisId());
  const isFirstTradeStateRef = useRef(true);
  const manualCloseRef = useRef(false);

  const suggestions = useMemo(
    () => buildSuggestions(currencyPair, tradeType),
    [currencyPair, tradeType],
  );
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [loading, messages]);

  useEffect(() => {
    return () => {
      manualCloseRef.current = true;
      try {
        socketRef.current?.close();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (isFirstTradeStateRef.current) {
      isFirstTradeStateRef.current = false;
      return;
    }

    try {
      manualCloseRef.current = true;
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createTradeAnalysisId();
    setMessages([]);
    setLoading(false);
    setSessionNotice(
      "Trade details changed, so a fresh Trade Analysis chat session is ready for the new context.",
    );
  }, [
    currencyPair,
    executionForwardPoints,
    executionSpotRate,
    executionTimestamp,
    expirationTimestamp,
    tradeType,
  ]);

  const updateAnswerMessage = (messageId, updater) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? updater(message) : message,
      ),
    );
  };

  const resetConversation = () => {
    try {
      manualCloseRef.current = true;
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createTradeAnalysisId();
    setMessages([]);
    setLoading(false);
    setMessageInput("");
    setSessionNotice("Started a fresh Trade Analysis conversation session.");
  };

  const copyMessage = async (htmlString) => {
    try {
      await navigator.clipboard.writeText(toPlainText(htmlString));
      alert("Copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const downloadMessage = async (htmlString) => {
    const container = document.createElement("div");
    container.innerHTML = htmlString;
    container.style.padding = "24px";
    container.style.fontFamily = "Arial, sans-serif";
    container.style.fontSize = "12px";
    document.body.appendChild(container);

    try {
      await html2pdf()
        .set({
          margin: 0.5,
          filename: `trade-analysis-report-${currencyPair.toLowerCase()}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      document.body.removeChild(container);
    }
  };

  const getFormattedAnswerHtml = (text) => {
    if (!text) return "";
    if (hasHtmlTags(text)) return text;
    return mdToHtml(text);
  };

  const handleSendMessage = async (rawInput, requestType = "conversation") => {
    if (loading) return;

    const userInput = (rawInput ?? messageInput).trim();
    if (!userInput) return;

    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("userId") || "anonymous";
    const timeZone = getUserTimeZone();

    if (!apiUrl) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          type: "answer",
          text: "<strong>Error:</strong> Missing <code>REACT_APP_API_URL</code> configuration.",
        },
      ]);
      return;
    }

    const questionId = `question-${Date.now()}`;
    const answerId = `answer-${Date.now()}`;

    const tradeContext = {
      analysis_surface: TRADE_ANALYSIS_SURFACE,
      origin: TRADE_ANALYSIS_ORIGIN,
      chat_id: chatIdRef.current,
      currency_pair: currencyPair ?? null,
      execution_timestamp: executionTimestamp ?? null,
      execution_timezone: timeZone,
      execution_spot_rate: normalizeNumber(executionSpotRate),
      userTimeZone: timeZone,
      user_language: userLanguage,
    };

    const requestBody =
      requestType === "market_window"
        ? {
            ...tradeContext,
            summary_profile: "market_window_context_v1",
            analysis_window: "1h",
            analysis_sources: MARKET_WINDOW_ANALYSIS_SOURCES,
          }
        : {
            ...tradeContext,
            analysis_mode: "conversation_v1",
            query: userInput,
            ...(tradeType ? { trade_type: tradeType } : {}),
            execution_forward_points: normalizeNumber(executionForwardPoints),
            expiration_timestamp: expirationTimestamp ?? "",
          };

    try {
      manualCloseRef.current = true;
      socketRef.current?.close();
    } catch {}

    setSessionNotice("");
    setLoading(true);
    setMessageInput("");
    setMessages((prev) => [
      ...prev,
      {
        id: questionId,
        type: "question",
        text: escapeHtml(userInput),
        raw: userInput,
        requestType,
      },
      {
        id: answerId,
        type: "answer",
        text: "",
        steps: PROCESSING_STEP,
        variant: "text",
      },
    ]);

    let ws;

    let hasFinished = false;
    let reconnectEnabled = true;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    let activeAssistantMessageId = null;

    const finalizeTurn = () => {
      reconnectEnabled = false;
      activeAssistantMessageId = null;
      setLoading(false);
        if (socketRef.current === ws) {
        socketRef.current = null;
      }
    };

    const finishTurn = () => {
      hasFinished = true;
      finalizeTurn();
      try {
        manualCloseRef.current = true;
        ws.close();
      } catch {}
    };

    const stopReconnectWithFallback = () => {
      updateAnswerMessage(answerId, (current) => ({
        ...current,
        variant: current.text ? current.variant : "text",
        text:
          current.text ||
          "<strong>Error:</strong> Trade Analysis lost the live stream before it could be resumed.",
      }));
      finalizeTurn();
    };

    const resetAnswerForReplay = () => {
        updateAnswerMessage(answerId, (current) => ({
        ...current,
        text: "",
        steps: PROCESSING_STEP,
        variant: "text",
      }));
    };

    const scheduleReconnect = () => {
      if (!reconnectEnabled || hasFinished) return;
      if (!activeAssistantMessageId) {
        stopReconnectWithFallback();
        return;
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        stopReconnectWithFallback();
        return;
      }
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
      reconnectAttempts += 1;
      window.setTimeout(() => {
        void openSocket(true).catch((error) => {
          console.error("Trade Analysis conversation reconnect auth error:", error);
          scheduleReconnect();
        });
      }, delay);
    };

    const openSocket = async (isReconnect = false) => {
      const wsUrl = await getAuthenticatedWebSocketUrl(
        chatIdRef.current,
        userId,
        isReconnect ? activeAssistantMessageId : undefined,
      );
      ws = new WebSocket(wsUrl);
      socketRef.current = ws;
      manualCloseRef.current = false;

      ws.onopen = () => {
        if (isReconnect) {
          resetAnswerForReplay();
          return;
        }
        ws.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        activeAssistantMessageId =
          data?.message_id ||
          (data?.type === "MESSAGE_ID" && data?.message != null
            ? String(data.message)
            : activeAssistantMessageId);

        const type = data?.type;
        const message = data?.message;

        if (
          type === "FINISH" ||
          type === "DONE" ||
          type === "END" ||
          type === "COMPLETE"
        ) {
          finishTurn();
          return;
        }

        if (type === "MESSAGE_ID" && message != null) {
          updateAnswerMessage(answerId, (current) => ({
            ...current,
            backendMessageId: String(message),
          }));
          return;
        }

        if (type === "INFO") {
          updateAnswerMessage(answerId, (current) => ({
            ...current,
            steps: `${current.steps || ""}${String(message || "")}\n`,
          }));
          return;
        }

        if (type === "SUMMARY_UPDATE" || type === "BESTX_STATE") {
          return;
        }

        if (message == null) {
          return;
        }

        if (type === "urls") {
          updateAnswerMessage(answerId, (current) => ({
            ...current,
            text: `${current.text}${formatUrlList(String(message))}`,
          }));
          return;
        }

        if (type === "BESTX_SUMMARY_HTML") {
          updateAnswerMessage(answerId, (current) =>
            appendSpecialPayload(
              current,
              "summary",
              "On-Demand Summary",
              String(message),
            ),
          );
          return;
        }

        if (type === "BESTX_REPORT_HTML") {
          updateAnswerMessage(answerId, (current) =>
            appendSpecialPayload(
              current,
              "report",
              "Final Report",
              String(message),
            ),
          );
          return;
        }

        if (type === "ERROR") {
          updateAnswerMessage(answerId, (current) => ({
            ...current,
            text: `${current.text}<div><strong>Error:</strong> ${String(message)}</div>`,
          }));
          return;
        }

        updateAnswerMessage(answerId, (current) => ({
          ...current,
          text: `${current.text}${String(message)}`,
        }));
      };

      ws.onerror = (error) => {
        console.error("Trade Analysis conversation WebSocket error:", error);
      };

      ws.onclose = () => {
        if (manualCloseRef.current) {
          manualCloseRef.current = false;
          if (socketRef.current === ws) {
            socketRef.current = null;
          }
          setLoading(false);
                return;
        }
        if (socketRef.current === ws) {
          socketRef.current = null;
        }
        if (!hasFinished && reconnectEnabled) {
          if (activeAssistantMessageId) {
            scheduleReconnect();
            return;
          }
          stopReconnectWithFallback();
          return;
        }
        setLoading(false);
          };
    };

    try {
      await openSocket(false);
    } catch (error) {
      console.error("Trade Analysis conversation auth error:", error);
      updateAnswerMessage(answerId, (current) => ({
        ...current,
        text:
          current.text ||
          "<strong>Error:</strong> Unable to authenticate the Trade Analysis conversation service.",
      }));
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleActionKeyDown = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  const stopGenerating = () => {
    stopListening();
    stopSpeaking();
    try {
      manualCloseRef.current = true;
      socketRef.current?.close();
    } catch {}
    socketRef.current = null;
    setLoading(false);
  };

  const clearChat = () => {
    clearSpeechError();
    stopListening();
    stopSpeaking();
    resetVoiceInput();
    setFeedbackTexts({});
    setFeedbackTypes({});
    resetConversation();
  };

  const regenerateAnswer = (index) => {
    const question = messages[index - 1];
    if (question?.type !== "question") return;
    handleSendMessage(question.raw, question.requestType);
  };

  const editQuestion = (index) => {
    const question = messages[index - 1];
    if (question?.type !== "question") return;
    setMessageInput(question.raw || "");
    document.getElementById("tradeAnalysisConversationInput")?.focus();
  };

  const openFeedbackForm = (messageId, feedbackType) => {
    setFeedbackTypes((prev) => ({ ...prev, [messageId]: feedbackType }));
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: "" }));
  };

  const submitFeedback = (messageId) => {
    const feedback = feedbackTypes[messageId] || "Negative";
    const feedbackText = feedbackTexts[messageId]?.trim() || feedback;
    giveFeedback({
      msgId: messageId,
      feedback,
      feedbackText,
      setFeedbackMessageId: setFeedbackDone,
    });
    setFeedbackTexts((prev) => {
      const { [messageId]: _removed, ...rest } = prev;
      return rest;
    });
    setFeedbackTypes((prev) => {
      const { [messageId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const lastAnswerForSpeech = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.type === "answer") {
        return {
          text: messages[i].text || "",
          streamKey: messages[i].id,
        };
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (!isTtsEnabled || !lastAnswerForSpeech) return;
    speakStreamingText(htmlToSpeechText(getFormattedAnswerHtml(lastAnswerForSpeech.text)), {
      isFinal: !loading,
      streamKey: lastAnswerForSpeech.streamKey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isTtsEnabled, lastAnswerForSpeech, speakStreamingText]);

  useEffect(
    () => () => {
      stopListening();
      stopSpeaking();
    },
    [stopListening, stopSpeaking],
  );

  return (
    <ChatOverlayFrame
      isOpen={isOverlayOpen}
      onClose={() => setIsOverlayOpen(false)}
    >
      <div
        ref={chatShellRef}
        data-tier={chatTier}
        className="w-100 h-100 d-flex flex-column ea-chat-surface ea-chat-shell ct-chat"
        style={{ minHeight: 0, overflow: "hidden" }}
      >
        <div className="ea-chat-header d-flex align-items-center justify-content-between gap-3 flex-wrap text-dark-gray">
          <div className="ea-chat-title-group">
            <span className="ea-chat-title">
              Trade Analysis Chat
              <span className="ct-chat-live">{currencyPair}</span>
            </span>
            <p className="ea-chat-subtitle mb-0">
              Conversation session with reusable trade context
            </p>
          </div>

          <div className="ea-chat-header-actions">
            <button
              type="button"
              className="btn btn-sm ea-chat-ghost-btn"
              onClick={() => setIsOverlayOpen((prev) => !prev)}
              title={isOverlayOpen ? "Exit full screen" : "Open full screen"}
              aria-pressed={isOverlayOpen}
            >
              <i
                className={`fa-solid ${
                  isOverlayOpen ? "fa-compress" : "fa-expand"
                } me-2`}
              />
              {isOverlayOpen ? "Exit" : "Expand"}
            </button>
            {messages.length > 0 && (
              <button
                type="button"
                className="btn btn-sm ea-chat-clear-btn"
                onClick={clearChat}
                disabled={loading}
              >
                Clear Chat
              </button>
            )}
          </div>
        </div>

        <div
          className="overflow-y-auto ea-chat-scroll"
          style={{ flex: 1, overflowX: "hidden" }}
        >
          {messages.length === 0 ? (
            <div className="flex-grow-1 ea-chat-empty">
              {sessionNotice && (
                <div className="ea-chat-bubble ea-chat-bubble--info mb-2">
                  <div className="ea-chat-rich">
                    <TradeAnalysisRenderFromString htmlString={sessionNotice} />
                  </div>
                </div>
              )}
              <div className="ct-suggestions" id="suggestion_section">
                {suggestions.map((suggestion) => (
                  <button
                    type="button"
                    className="ct-suggestion"
                    key={suggestion.prompt}
                    title={suggestion.prompt}
                    onClick={() =>
                      handleSendMessage(suggestion.prompt, suggestion.requestType)
                    }
                  >
                    <span>{suggestion.prompt}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-grow-1 overflow-auto">
              {messages.map((message, index) => {
                const isQuestion = message.type === "question";
                const isStreamingAnswer =
                  loading && !isQuestion && index === messages.length - 1;
                const answerHtml = isQuestion
                  ? message.text
                  : `${
                      message.steps
                        ? `<CollapsibleDiv>${message.steps}${
                            isStreamingAnswer && !message.text
                              ? ""
                              : "</CollapsibleDiv>"
                          }`
                        : ""
                    }${getFormattedAnswerHtml(message.text)}`;
                const backendId = message.backendMessageId;

                return (
                  <div
                    key={message.id}
                    className={`my-2 ea-chat-bubble ${
                      isQuestion
                        ? "ea-chat-bubble--question text-end"
                        : "ea-chat-bubble--answer text-start"
                    }`}
                    style={{
                      maxWidth: isQuestion ? "70%" : "100%",
                      marginLeft: isQuestion ? "auto" : "0",
                      marginRight: isQuestion ? "0" : "auto",
                    }}
                  >
                    <div className="ea-chat-rich">
                      <RenderFromString
                        htmlString={answerHtml}
                        isStreaming={isStreamingAnswer}
                      />
                    </div>

                    {!isQuestion && !loading && message.text && (
                      <div className="ea-chat-actions">
                        <div className="ea-chat-action-group">
                          {backendId && !feedbackDone.includes(backendId) && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success ea-chat-icon-btn"
                                onClick={() => openFeedbackForm(backendId, "Positive")}
                              >
                                <i className="fa-solid fa-thumbs-up" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ea-chat-icon-btn"
                                onClick={() => openFeedbackForm(backendId, "Negative")}
                              >
                                <i className="fa-solid fa-thumbs-down" />
                              </button>
                            </>
                          )}
                        </div>

                        <div className="ea-chat-action-group ms-auto">
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => copyMessage(message.text)}
                            onKeyDown={(event) =>
                              handleActionKeyDown(event, () => copyMessage(message.text))
                            }
                            title="Copy Message"
                          >
                            <FaCopy aria-hidden="true" />
                          </span>
                          {message.variant === "report" && (
                            <span
                              className="ea-chat-action-link"
                              role="button"
                              tabIndex={0}
                              onClick={() => downloadMessage(message.text)}
                              onKeyDown={(event) =>
                                handleActionKeyDown(event, () =>
                                  downloadMessage(message.text),
                                )
                              }
                              title="Download Report"
                            >
                              <FaDownload aria-hidden="true" />
                            </span>
                          )}
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            title="Edit Message"
                            onClick={() => editQuestion(index)}
                            onKeyDown={(event) =>
                              handleActionKeyDown(event, () => editQuestion(index))
                            }
                          >
                            <FaPen aria-hidden="true" />
                          </span>
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            title="Regenerate Answer"
                            onClick={() => regenerateAnswer(index)}
                            onKeyDown={(event) =>
                              handleActionKeyDown(event, () => regenerateAnswer(index))
                            }
                          >
                            <FaRedo aria-hidden="true" />
                          </span>
                        </div>
                      </div>
                    )}

                    {backendId && feedbackTexts[backendId] !== undefined && (
                      <div className="mt-2 w-100">
                        <textarea
                          className="form-control ea-chat-feedback-textarea"
                          rows={2}
                          placeholder={
                            feedbackTypes[backendId] === "Positive"
                              ? "Tell us what worked well..."
                              : "Tell us what went wrong..."
                          }
                          value={feedbackTexts[backendId]}
                          onChange={(event) =>
                            setFeedbackTexts((prev) => ({
                              ...prev,
                              [backendId]: event.target.value,
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-sm mt-1 ea-chat-feedback-submit"
                          onClick={() => submitFeedback(backendId)}
                        >
                          Submit Feedback
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="w-100 ea-composer is-docked">
          {speechError && <div className="ct-chat-hint is-error">{speechError}</div>}
          {isListening && !loading && (
            <div className="ct-chat-hint">
              Listening... auto-send after 1s of silence.
            </div>
          )}
          <div className="ea-composer__field">
            <div className="ea-composer__input-wrap">
              <input
                type="text"
                placeholder={`Ask about this ${currencyPair} trade`}
                aria-label="Type your question"
                id="tradeAnalysisConversationInput"
                className="form-control ea-composer__input"
                value={messageInput}
                onChange={(event) => setMessageInput(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="off"
              />
            </div>
            {isSpeechRecognitionSupported && (
              <button
                type="button"
                className={`ea-composer__icon ${isListening ? "is-active" : ""}`}
                onClick={toggleListening}
                title={isListening ? "Stop voice input" : "Voice input"}
                aria-label={isListening ? "Stop voice input" : "Start voice input"}
                aria-pressed={isListening}
                disabled={loading}
              >
                <i className="fa-solid fa-microphone" aria-hidden="true" />
              </button>
            )}
            {isSpeechSynthesisSupported && (
              <button
                type="button"
                className="ea-composer__icon"
                onClick={toggleTts}
                title={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
                aria-label={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
                aria-pressed={isTtsEnabled}
                style={isTtsEnabled ? { color: "#067647" } : undefined}
              >
                <i
                  className={`fa-solid ${isTtsEnabled ? "fa-volume-high" : "fa-volume-xmark"}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {loading ? (
              <button
                type="button"
                className="ea-composer__send is-stop"
                title="Stop generating"
                aria-label="Stop generating"
                onClick={stopGenerating}
              >
                <span className="ea-composer__stop-glyph" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="ea-composer__send"
                onClick={() => handleSendMessage()}
                disabled={!messageInput.trim()}
                title="Send"
                aria-label="Send message"
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </ChatOverlayFrame>
  );
};

const TradeAnalysis = () => {
  const { isDark, isMobile } = useTheme();
  const palette = useMemo(() => getPalette(isDark), [isDark]);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [activeChartTab, setActiveChartTab] = useState("prices");
  const chartAreaRef = useRef(null);
  const [chartAreaHeight, setChartAreaHeight] = useState(
    TRADE_ANALYSIS_CHART_HEIGHT,
  );
  const [useCompactCharts, setUseCompactCharts] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 580,
  );

  const [currencyPair, setCurrencyPair] = useState("");
  const [tradeType, setTradeType] = useState("BUY");
  const [useContext, setUseContext] = useState(true);

  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState(null);
  const [executionForwardPoints, setExecutionForwardPoints] = useState(null);
  const [expirationTimestamp, setExpirationTimestamp] = useState("");

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const summaryCacheRef = useRef("");
  const summaryVersionRef = useRef(0);

  const [userLanguage, setUserLanguage] = useState(
    localStorage.getItem("user_language") || "English",
  );
  const [tenorList, setTenorList] = useState([]);
  const [tenors, setTenors] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadCurrencyList = async () => {
      try {
        const data = await fetchFxSymbols();
        if (!cancelled) {
          setCurrencyList(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching currency list:", error);
      }
    };

    loadCurrencyList();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 580px)");
    const handleChange = (event) => {
      setUseCompactCharts(event.matches);
    };

    setUseCompactCharts(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Desktop charts fill the bottom panel, so follow its height while resizing.
  useEffect(() => {
    const node = chartAreaRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.floor(entry.contentRect.height);
      if (next > 0) setChartAreaHeight(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasSubmitted, isMobile, useCompactCharts]);

  useEffect(() => {
    if (!currencyPair && useContext) return;
    if (!currencyPair) return;
    setSessionVariable(currencyPair);
  }, [currencyPair, useContext]);

  useEffect(() => {
    if (!hasSubmitted || !currencyPair || !executionTimestamp) return;

    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;

    const cleanup = handleFetchSummary({
      currencyPair,
      tradeType,
      executionTimestamp,
      executionSpotRate,
      expirationTimestamp,
      executionForwardPoints,
      setSummary,
      summaryCacheRef,
      summaryVersionRef,
      setLoading,
    });

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [
    currencyPair,
    executionForwardPoints,
    executionSpotRate,
    executionTimestamp,
    expirationTimestamp,
    hasSubmitted,
    tradeType,
  ]);

  const handleTradeSubmit = (trade) => {
    setCurrencyPair(trade.currencyPair);
    setTradeType(trade.tradeType);
    setExecutionTimestamp(trade.executionTimestamp);
    setExecutionSpotRate(trade.executionSpotRate);
    setExecutionForwardPoints(trade.executionForwardPoints);
    setExpirationTimestamp(trade.expirationTimestamp);
    setUseContext(false);
    setHasSubmitted(true);
  };

  const handleResetToLanding = () => {
    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setHasSubmitted(false);
    setUseContext(true);
    setCurrencyPair("");
    setTradeType("BUY");
    setExecutionTimestamp("");
    setExecutionSpotRate(null);
    setExecutionForwardPoints(null);
    setExpirationTimestamp("");
    setSummary("");
    setLoading(false);
    setTenorList([]);
    setTenors([]);
    setActiveTab("summary");
    setActiveChartTab("prices");

    localStorage.removeItem("trade_analysis_exec_ts");
    localStorage.removeItem("trade_analysis_exec_rate");
    localStorage.removeItem("trade_analysis_exec_fwd");
    localStorage.removeItem("trade_analysis_expiry");
  };

  const handleSetTenorList = useCallback((nextTenorList = []) => {
    const filteredTenors = getCanonicalForwardTenors(nextTenorList);
    setTenorList((previousTenors) =>
      areArraysEqual(previousTenors, filteredTenors)
        ? previousTenors
        : filteredTenors,
    );
  }, []);

  if (!hasSubmitted) {
    return (
      <TradeAnalysisLandingPage
        currencyList={currencyList}
        onSubmit={handleTradeSubmit}
        palette={palette}
        isMobile={isMobile}
      />
    );
  }

  const tabButtonBaseStyle = {
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: "999px",
    padding: "7px 14px",
    background: palette.cardInnerBg,
    color: palette.textPrimary,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.78rem",
  };
    const renderChartsSection = (fill = false) => (
    <div
      className={fill ? "d-flex flex-column" : undefined}
      style={{
        borderRadius: "12px",
        border: `1px solid ${palette.cardBorder}`,
        backgroundColor: palette.cardBg,
        padding: "12px",
        minHeight: 0,
        overflow: fill && useCompactCharts ? "auto" : "hidden",
        ...(fill ? { height: "100%" } : { flexShrink: 0 }),
      }}
    >
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
        <div>
          <p
            className="mb-0"
            style={{ color: palette.textPrimary, fontWeight: 700, fontSize: "0.92rem" }}
          >
            Market Context
          </p>
          <p
            className="mb-0"
            style={{ color: palette.textSecondary, fontSize: "0.76rem" }}
          >
            Spot and forward behavior around the execution window
          </p>
        </div>
      </div>

      {useCompactCharts ? (
        <>
          <div className="d-flex gap-2 flex-wrap mb-2">
            <button
              type="button"
              style={{
                ...tabButtonBaseStyle,
                ...(activeChartTab === "prices"
                  ? {
                      backgroundColor: palette.accent,
                      borderColor: palette.accent,
                      color: "#fff",
                    }
                  : null),
              }}
              onClick={() => setActiveChartTab("prices")}
            >
              Spot Prices
            </button>
            <button
              type="button"
              style={{
                ...tabButtonBaseStyle,
                ...(activeChartTab === "forward"
                  ? {
                      backgroundColor: palette.accent,
                      borderColor: palette.accent,
                      color: "#fff",
                    }
                  : null),
              }}
              onClick={() => setActiveChartTab("forward")}
            >
              Forward Points
            </button>
          </div>
          <div
            style={{
              borderRadius: "10px",
              border: `1px solid ${palette.cardBorder}`,
              backgroundColor: palette.cardBg,
              minHeight: "320px",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <p
              className="mb-2"
              style={{ color: palette.textPrimary, fontWeight: 700, fontSize: "0.8rem" }}
            >
              {activeChartTab === "prices" ? "Spot Prices" : "Forward Points"}
            </p>
            <div
              style={{
                height: `${TRADE_ANALYSIS_CHART_HEIGHT}px`,
                width: "100%",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {activeChartTab === "prices" ? (
                <SpotPriceChart
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  isDark={isDark}
                />
              ) : (
                <ForwardPointsChart
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  setTenorList={handleSetTenorList}
                  tenors={tenors}
                  isDark={isDark}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <div className={`row g-2 ${fill ? "flex-grow-1" : ""}`} style={fill ? { minHeight: 0 } : undefined}>
          <div className="col-12 col-lg-6" style={fill ? { height: "100%" } : undefined}>
            <div
              style={{
                borderRadius: "10px",
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: palette.cardBg,
                minHeight: fill ? 0 : "320px",
                padding: "10px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <p
                className="mb-2"
                style={{ color: palette.textPrimary, fontWeight: 700, fontSize: "0.8rem" }}
              >
                Spot Prices
              </p>
              <div
              ref={chartAreaRef}
              style={fill ? { flex: "1 1 0", minHeight: 0, width: "100%", overflow: "hidden" } : {
                height: `${TRADE_ANALYSIS_CHART_HEIGHT}px`,
                width: "100%",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
                <SpotPriceChart
                  height={fill ? chartAreaHeight : TRADE_ANALYSIS_CHART_HEIGHT}
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  isDark={isDark}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6" style={fill ? { height: "100%" } : undefined}>
            <div
              style={{
                borderRadius: "10px",
                border: `1px solid ${palette.cardBorder}`,
                backgroundColor: palette.cardBg,
                minHeight: fill ? 0 : "320px",
                padding: "10px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <p
                className="mb-2"
                style={{ color: palette.textPrimary, fontWeight: 700, fontSize: "0.8rem" }}
              >
                Forward Points
              </p>
              <div
              style={fill ? { flex: "1 1 0", minHeight: 0, width: "100%", overflow: "hidden" } : {
                height: `${TRADE_ANALYSIS_CHART_HEIGHT}px`,
                width: "100%",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
                <ForwardPointsChart
                  height={fill ? chartAreaHeight : TRADE_ANALYSIS_CHART_HEIGHT}
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  setTenorList={handleSetTenorList}
                  tenors={tenors}
                  isDark={isDark}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="w-100 h-100 d-flex flex-column p-3 ct-page ta-page"
      style={{
        backgroundColor: palette.pageBg,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
        style={{
          borderRadius: "10px",
          border: `1px solid ${palette.cardBorder}`,
          backgroundColor: palette.cardBg,
          padding: "10px",
          marginBottom: "10px",
          flexShrink: 0,
        }}
      >
        <TradeAnalysisTradeHeader
          currencyPair={currencyPair}
          tradeType={tradeType}
          executionTimestampUtc={executionTimestamp}
          executionSpotRate={executionSpotRate}
          executionForwardPoints={executionForwardPoints}
          expiryDateUtc={expirationTimestamp}
          palette={palette}
          isMobile={isMobile}
        />

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleResetToLanding}
            style={{
              border: `1px solid ${palette.cardBorder}`,
              color: palette.textSecondary,
              backgroundColor: palette.cardInnerBg,
              fontWeight: 700,
            }}
          >
            Reset
          </button>
          <TradeAnalysisPopOverMenu
            setCurrencyPair={setCurrencyPair}
            setUseContext={setUseContext}
            useContext={useContext}
            currencyPair={currencyPair}
            currencyList={currencyList}
            tenorList={tenorList}
            setTenor={setTenors}
            setUserLanguage={setUserLanguage}
            userLanguage={userLanguage}
            setExecutionTimestamp={setExecutionTimestamp}
            setExecutionSpotRate={setExecutionSpotRate}
            setExpirationDate={setExpirationTimestamp}
            setExecutionForwardPoints={setExecutionForwardPoints}
            tradeType={tradeType}
            setTradeType={setTradeType}
            executionTimestamp={executionTimestamp}
            executionSpotRate={executionSpotRate}
            executionForwardPoints={executionForwardPoints}
            expirationTimestamp={expirationTimestamp}
            palette={palette}
            isMobile={isMobile}
          />
        </div>
      </div>

      {!isMobile ? (
        <PanelGroup
          direction="vertical"
          autoSaveId="trade-analysis-vertical-charts-top"
          className="flex-grow-1"
          style={{ minHeight: 0 }}
        >
          <Panel defaultSize={40} minSize={20} style={{ minHeight: 0 }}>
            {renderChartsSection(true)}
          </Panel>
          <PanelResizeHandle
            className="ct-resize-handle"
            title="Drag to resize the panels"
          >
            <span className="ct-resize-grip" aria-hidden="true" />
          </PanelResizeHandle>
          <Panel defaultSize={60} minSize={30} style={{ minHeight: 0 }}>
          <div
            className="row gx-2 gy-0"
            style={{ minHeight: 0, height: "100%", marginLeft: "-0.25rem", marginRight: "-0.25rem", marginTop: 0, marginBottom: 0 }}
          >
            <div
              className="col-12 col-xl-6 d-flex"
              style={{ minHeight: 0, height: "100%", overflow: "hidden" }}
            >
              <TradeAnalysisSummary summary={summary} loading={loading} palette={palette} />
            </div>
            <div
              className="col-12 col-xl-6 d-flex"
              style={{ minHeight: 0, height: "100%", overflow: "hidden" }}
            >
              <TradeAnalysisConversation
                currencyPair={currencyPair}
                tradeType={tradeType}
                executionTimestamp={executionTimestamp}
                executionSpotRate={executionSpotRate}
                executionForwardPoints={executionForwardPoints}
                expirationTimestamp={expirationTimestamp}
                userLanguage={userLanguage}
                palette={palette}
              />
            </div>
          </div>
          </Panel>
        </PanelGroup>
      ) : (
        <>
          <div className="d-flex gap-2 flex-wrap mb-2" style={{ flexShrink: 0 }}>
            <button
              type="button"
              style={{
                ...tabButtonBaseStyle,
                ...(activeTab === "summary"
                  ? {
                      backgroundColor: palette.accent,
                      borderColor: palette.accent,
                      color: "#fff",
                    }
                  : null),
              }}
              onClick={() => setActiveTab("summary")}
            >
              Summary
            </button>

            <button
              type="button"
              style={{
                ...tabButtonBaseStyle,
                ...(activeTab === "chat"
                  ? {
                      backgroundColor: palette.accent,
                      borderColor: palette.accent,
                      color: "#fff",
                    }
                  : null),
              }}
              onClick={() => setActiveTab("chat")}
            >
              Chat
            </button>
          </div>

          <div
            className="flex-grow-1 d-flex flex-column"
            style={{ minHeight: 0, gap: "10px", overflow: "auto" }}
          >
            <div className="flex-grow-1" style={{ overflow: "hidden", minHeight: 0 }}>
              <div
                style={{
                  display: activeTab === "summary" ? "block" : "none",
                  height: "100%",
                  overflow: "auto",
                }}
              >
                <TradeAnalysisSummary summary={summary} loading={loading} palette={palette} />
              </div>
              <div
                style={{
                  display: activeTab === "chat" ? "block" : "none",
                  height: "100%",
                  overflow: "auto",
                }}
              >
                <TradeAnalysisConversation
                  currencyPair={currencyPair}
                  tradeType={tradeType}
                  executionTimestamp={executionTimestamp}
                  executionSpotRate={executionSpotRate}
                  executionForwardPoints={executionForwardPoints}
                  expirationTimestamp={expirationTimestamp}
                  userLanguage={userLanguage}
                  palette={palette}
                />
              </div>
            </div>
            {renderChartsSection()}
          </div>
        </>
      )}
    </div>
  );
};

export default TradeAnalysis;

const handleFetchSummary = ({
  setSummary,
  summaryCacheRef,
  summaryVersionRef,
  currencyPair,
  tradeType,
  executionTimestamp,
  executionSpotRate,
  expirationTimestamp,
  setLoading,
  executionForwardPoints,
}) => {
  setLoading(true);
  setSummary("");

  const userId = localStorage.getItem("userId") || "anonymous";
  const userTimeZone = getUserTimeZone();
  const userLanguage = localStorage.getItem("user_language") || "English";
  const chatId = createTradeAnalysisId();

  const requestBody = {
    analysis_surface: TRADE_ANALYSIS_SURFACE,
    origin: TRADE_ANALYSIS_ORIGIN,
    chat_id: chatId,
    currency_pair: currencyPair ?? null,
    execution_timestamp: executionTimestamp ?? null,
    execution_timezone: userTimeZone,
    execution_spot_rate: normalizeNumber(executionSpotRate),
    execution_forward_points: normalizeNumber(executionForwardPoints),
    expiration_timestamp: expirationTimestamp ?? "",
    userTimeZone,
    user_language: userLanguage,
    embedding_model: "text-embedding-ada-002",
    ...(tradeType ? { trade_type: tradeType } : {}),
  };

  let isCancelled = false;
  let ws = null;
  let hasFinished = false;
  let reconnectEnabled = true;
  let reconnectAttempts = 0;
  const MAX_RECONNECT_ATTEMPTS = 5;
  let activeAssistantMessageId = null;

  const stopStreaming = () => {
    reconnectEnabled = false;
    activeAssistantMessageId = null;
    setLoading(false);
  };

  const resetSummaryForReplay = () => {
    setSummary("");
  };

  const scheduleReconnect = () => {
    if (!reconnectEnabled || hasFinished || isCancelled) return;
    if (!activeAssistantMessageId) {
      stopStreaming();
      return;
    }
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      stopStreaming();
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
    reconnectAttempts += 1;
    window.setTimeout(() => {
      void openSocket(true).catch((error) => {
        console.error("Trade Analysis summary reconnect auth failed:", error);
        scheduleReconnect();
      });
    }, delay);
  };

  const openSocket = async (isReconnect = false) => {
    const wsUrl = await getAuthenticatedWebSocketUrl(
      chatId,
      userId,
      isReconnect ? activeAssistantMessageId : undefined,
    );
    if (isCancelled) return;

    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      if (isReconnect) {
        resetSummaryForReplay();
        return;
      }
      ws?.send(JSON.stringify(requestBody));
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      activeAssistantMessageId =
        data?.message_id ||
        (data?.type === "MESSAGE_ID" && data?.message != null
          ? String(data.message)
          : activeAssistantMessageId);

      const type = data?.type;
      const message = data?.message;

      if (
        type === "FINISH" ||
        type === "DONE" ||
        type === "END" ||
        type === "COMPLETE"
      ) {
        hasFinished = true;
        stopStreaming();
        try {
          ws?.close();
        } catch {}
        return;
      }

      if (type === "SUMMARY_UPDATE") {
        const summaryPayload = parseSummaryPayload(message);
        summaryCacheRef.current = summaryPayload?.summary || "";
        summaryVersionRef.current = summaryPayload?.version || 0;
        return;
      }

      if (type === "INFO" || type === "ERROR" || type === "MESSAGE_ID") {
        return;
      }

      if (message != null) {
        setSummary((prev) => prev + String(message));
      }
    };

    ws.onerror = (error) => {
      console.error("Trade Analysis summary websocket error:", error);
    };

    ws.onclose = () => {
      if (!hasFinished && reconnectEnabled && !isCancelled) {
        if (activeAssistantMessageId) {
          scheduleReconnect();
          return;
        }
        stopStreaming();
        return;
      }
      setLoading(false);
    };
  };

  void openSocket().catch((error) => {
    if (!isCancelled) {
      console.error("Trade Analysis summary auth failed:", error);
      setLoading(false);
    }
  });

  return () => {
    isCancelled = true;
    try {
      ws?.close();
    } catch {}
  };
};
